
import { useNavigate } from "react-router-dom";
import { EventBasicInfo } from "./EventBasicInfo";
import { useToast } from "@/hooks/use-toast";
import { Event } from "@/types/event";
import { useQueryClient } from "@tanstack/react-query";
import { EventDetailsAccordion } from "./accordion/EventDetailsAccordion";
import { useState } from "react";
import { EventViewFooter } from "./EventViewFooter";

interface EventViewProps {
  event: Event;
  isHost: boolean;
  onEditClick?: () => void;
  onRemoveGuest?: (userId: string) => void;
  rsvpStatus?: 'invited' | 'accepted' | 'declined' | 'maybe';
}

export const EventView = ({
  event,
  isHost,
  onEditClick,
  onRemoveGuest,
  rsvpStatus
}: EventViewProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const handleRemoveInvite = async (inviteId: string) => {
    if (!onRemoveGuest) return;
    try {
      await onRemoveGuest(inviteId);
      queryClient.invalidateQueries({
        queryKey: ['event-invites', event.event_id]
      });
      toast({
        title: "Success",
        description: "Guest removed successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove guest",
        variant: "destructive"
      });
    }
  };
  
  const handleRsvp = () => {
    navigate(`/events/${event.event_id}/rsvp`);
  };
  
  const attendees = event.attendees || [];
  
  return <div className="mx-auto pb-24 relative">
      <div className="space-y-6">
        <EventBasicInfo 
          name={event.name} 
          description={event.description || ""} 
          eventDate={event.event_date} 
          imageUrl={event.image_url} 
          status={event.status} 
          locationType={event.location_type} 
          flexibleLocationDescription={event.flexible_location_description || ""} 
          preferredDates={event.preferred_dates} 
          preferredVenues={event.preferred_venues} 
          preferredTimeOfDay={event.preferred_time_of_day} 
          selectedVenue={event.selectedVenue} 
          selectedDate={event.selectedDate} 
          host={event.host ? {
            full_name: event.host.full_name,
            avatar_url: event.host.avatar_url
          } : undefined} 
          isHost={isHost} 
          eventId={event.event_id} 
          eventImages={event.additional_images} 
          event_deadline={event.event_deadline} 
          rsvpStatus={!isHost ? rsvpStatus : undefined} 
          guestRsvpComplete={rsvpStatus === 'accepted' || rsvpStatus === 'declined' || rsvpStatus === 'maybe'} 
          hasRecommendations={event.event_date_recommendations && event.event_date_recommendations.length > 0} 
          onEdit={onEditClick} 
          onRsvp={handleRsvp}
          attendees={attendees}
          event={event} // Pass the full event object
        />
        
        <div className="px-4">
          <EventDetailsAccordion 
            event={event} 
            isHost={isHost} 
            onRemoveGuest={handleRemoveInvite} 
            onEdit={onEditClick} 
            rsvpStatus={rsvpStatus} 
          />
        </div>
      </div>
      
      {/* Replace the old footer with our new component */}
      <EventViewFooter event={event} isHost={isHost} />
    </div>;
};
